import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IonPage,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonItem,
  IonInput,
  IonButton,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonIcon,
  IonSpinner,
  IonNote,
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/auth.store';
import { apiErrorMessage } from '@/lib/api';
import { validateAuthForm, type AuthMode } from '@/lib/auth-form';
import { toast } from '@/store/ui.store';

export default function LoginPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);

  const [mode, setMode] = useState<AuthMode>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * The password manager writes straight into the element and fires no
   * `ionInput`, so `email`/`password` state can be empty while the fields are
   * visibly filled. The elements are the source of truth at submit time.
   */
  const nameRef = useRef<HTMLIonInputElement>(null);
  const emailRef = useRef<HTMLIonInputElement>(null);
  const passwordRef = useRef<HTMLIonInputElement>(null);

  /**
   * Invite code carried by a shared `/login?ref=AIM-XXXX-XXXX` link. Read once
   * on mount and passed with the registration; the server attributes the account
   * on a best-effort basis, so a bad code never blocks the signup itself.
   */
  const referralCode = useMemo(
    () => new URLSearchParams(location.search).get('ref')?.trim() || undefined,
    [location.search],
  );

  useEffect(() => {
    // Surface the invite immediately so the customer knows why they landed here.
    if (referralCode) setMode('signup');
  }, [referralCode]);

  const submit = async () => {
    // `IonInput.value` is typed `string | number | null | undefined`.
    const readValue = (element: HTMLIonInputElement | null, fallback: string): string => {
      const raw = element?.value ?? fallback;
      return typeof raw === 'string' ? raw : String(raw);
    };
    const values = {
      name: readValue(nameRef.current, name),
      email: readValue(emailRef.current, email),
      password: readValue(passwordRef.current, password),
    };
    const problem = validateAuthForm(mode, values);
    if (problem) {
      toast(t(problem.messageKey, problem.params), 'warning');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'signin') {
        await login(values.email.trim(), values.password);
      } else {
        await register(values.name.trim(), values.email.trim(), values.password, referralCode);
      }
      history.replace('/app');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="login-shell">
          <div className="empty-state" style={{ padding: '16px 0 28px' }}>
            <img src="/logo.png" alt="Social Media Autopilot" className="login-logo" />
            <h4 style={{ fontWeight: 800, margin: '4px 0' }}>Social Media Autopilot</h4>
            <p style={{ margin: 0 }}>Social autopilot powered by your own GPU</p>
          </div>

          <IonCard>
            <IonCardHeader>
              <IonSegment
                value={mode}
                onIonChange={(event) => setMode((event.target as HTMLIonSegmentElement).value as 'signin' | 'signup')}
              >
                <IonSegmentButton value="signin">
                  <IonLabel>{t('auth.signIn')}</IonLabel>
                </IonSegmentButton>
                <IonSegmentButton value="signup">
                  <IonLabel>{t('auth.signUp')}</IonLabel>
                </IonSegmentButton>
              </IonSegment>
            </IonCardHeader>

            <div style={{ padding: '4px 8px 12px' }}>
              {mode === 'signup' && referralCode && (
                <IonNote color="primary">{t('billing.invitedBy', { code: referralCode })}</IonNote>
              )}
              {mode === 'signup' && (
                <IonItem>
                  <IonInput
                    ref={nameRef}
                    label={t('auth.name')}
                    labelPlacement="stacked"
                    value={name}
                    onIonInput={(event) => setName((event.target as HTMLIonInputElement).value as string)}
                  />
                </IonItem>
              )}
              <IonItem>
                <IonInput
                  ref={emailRef}
                  label={`${t('auth.email')} *`}
                  labelPlacement="stacked"
                  type="email"
                  name="email"
                  autocomplete="email"
                  value={email}
                  onIonInput={(event) => setEmail((event.target as HTMLIonInputElement).value as string)}
                />
              </IonItem>
              <IonItem>
                <IonInput
                  ref={passwordRef}
                  label={`${t('auth.password')} *`}
                  labelPlacement="stacked"
                  type="password"
                  name="password"
                  autocomplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  value={password}
                  onIonInput={(event) => setPassword((event.target as HTMLIonInputElement).value as string)}
                />
              </IonItem>
              <IonButton expand="block" className="ion-margin-top" disabled={busy} onClick={() => void submit()}>
                {busy ? <IonSpinner name="dots" /> : mode === 'signin' ? t('auth.signInWithEmail') : t('auth.signUp')}
              </IonButton>
            </div>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
}
